import { type Message, strings } from "@quickpoint.me/shared";
import {
	type ReactElement,
	memo,
	useCallback,
	useContext,
	useLayoutEffect,
	useRef,
	useState,
} from "react";
import { useRecoilValue } from "recoil";
import styled, { ThemeProvider } from "styled-components";
import { elementAlign, opacityHelper } from "../../lib/cssHelpers";
import {
	PageElementsFamily,
	PageSizeAtom,
	createLocation,
} from "../../state/page";
import { HostId, TapDirection } from "../../state/ui";

import type { ElementProps } from "../../functionalComponents/RenderPage";
import { mainCssRules } from "../../lib/cssTemplates";
import { CurrentFilter, FilterColors } from "../../state/filter";

import type { ImageProps } from "../../@types/viewerati";
import useAiAnimation from "../../lib/animations/aiAnimationHook";
import { useElementAnimation } from "../../lib/animations/elementAnimationHook";
import useTappedAnimation from "../../lib/animations/tappedAnimationHook";
import { useClickAnimation } from "../../lib/animations/useClickAnimation";
import modifierStyled from "../../lib/modifierStyled";
import { NeuroDivergentFontMultiplier } from "../../state/accessibility";
import { AllLoaded, OwnerName } from "../../state/selectors";
import { IsInFlexBoxContext } from "../displayElements/Cell";
import { PositionContext } from "../displayElements/DisplayElements";
import { StyledCaption } from "./Caption";
import Image from "./Image";

const StyledFigure = styled.figure<ImageProps & { $fontMultiplier: number; $baseFontSizeRaw: number }>`
  ${(props) => elementAlign(props.theme.align, props.$isInFlexBox)};
  align-items: center;
  display: flex;
  width: 100%;
  max-width: 100%;
  visibility: ${(props) => (props.$tapped ? "visible" : "hidden")};
  flex-direction: column;
  flex-shrink: 1;
  height: ${(props) =>
		props.$isInFlexBox || !props.$isInLandscape
			? "auto"
			: "100%"}; // seems to fix safari
  ${(props) => (props.$isInFlexBox ? "min-height: 0;" : "")} // min height 0 when in a cell?
  ${(props) =>
		props.$isInFlexBox
			? "height: auto;"
			: props.$isInLandscape
				? "min-height: 0;"
				: ""}
  opacity: ${(props) => opacityHelper(props.$tapped, props.theme.opacity)};
  position: relative;
  ${(props) =>
		`${mainCssRules({
			baseFontSize: props.$baseFontSize,
			baseFontSizeRaw: props.$baseFontSizeRaw,
			theme: props.theme,
			fontMultiplier: props.$fontMultiplier,
		})}`}

  ${(props) => `${modifierStyled("image:", props)}`}
  border: 0;
`;

const Figure = ({
	atomId,
	pageIndex,
	type,
}: ElementProps): ReactElement<ElementProps> => {
	const key = createLocation(pageIndex, atomId);

	const elementData = useRecoilValue(PageElementsFamily(key)).data;
	const figureRef = useRef<HTMLImageElement>(null);
	const imageRef = useRef<HTMLImageElement>(null);

	const animation = elementData.modifier?.["animate:"];
	const filter = useRecoilValue(CurrentFilter);
	const filterColors = useRecoilValue(FilterColors);
	const fontMultiplier = useRecoilValue(NeuroDivergentFontMultiplier);
	const imageId = `imageComponent-${elementData.id}`;
	const isInFlexBox = useContext(IsInFlexBoxContext);
	const pageSize = useRecoilValue(PageSizeAtom);
	const tapped = elementData.tapped;
	const [imageWidth, setImageWidth] = useState(0);

	const baseFontSize = pageSize.baseFontSize;
	const caption = elementData.modifier
		? elementData.modifier["caption:"]
		: undefined;
	const direction = useRecoilValue(TapDirection);

	const allLoaded = useRecoilValue(AllLoaded);
	const hostId = useRecoilValue(HostId);
	const ownerName = useRecoilValue(OwnerName);
	const position = useContext(PositionContext);
	const toPage = elementData.modifier?.["toPage:"];
	const url = elementData.modifier?.["url:"];
	const backlink = elementData.modifier?.["backlink:"];

	useTappedAnimation({ elRef: figureRef, tapped, atomId }); // Apply tap animation to figure
	useAiAnimation({ elRef: figureRef, modifier: elementData.modifier }); // Apply AI animation to figure
	useElementAnimation({
		ref: figureRef,
		tweenName: animation,
		onePercent: {
			width: pageSize.onePercentWidth,
			height: pageSize.onePercentHeight,
		},
		options: {
			position: elementData.modifier?.["animateStart:"]
				? elementData.modifier?.["animateStart:"]
				: undefined,
		},
		position,
		direction,
	});

	const handleNavigation = useCallback(() => {
		let bc: BroadcastChannel | null = null;
		try {
			if (url) {
				const safeUrl =
					url.startsWith("http://") || url.startsWith("https://")
						? url
						: `https://${url}`;
				window.open(safeUrl, "_blank", "noopener,noreferrer");
			} else if (backlink) {
				window.location.href = `/${ownerName}/${backlink}`;
			} else if (toPage) {
				bc = new BroadcastChannel(strings.broadcastChannel.viewer);
				const pageNumber = Number.parseInt(toPage, 10);
				if (!Number.isNaN(pageNumber)) {
					const message: Message = {
						hostId,
						type: "QP",
						action: strings.messages.goToPage,
						payload: pageNumber,
					};
					bc.postMessage(message);
				} else {
					console.warn(`Invalid page number for toPage: ${toPage}`);
				}
			}
		} catch (error) {
			console.error("Error during navigation click:", error);
		} finally {
			if (bc) {
				bc.close();
			}
		}
	}, [url, backlink, toPage, ownerName, hostId]);

	useClickAnimation({
		figureRef,
		imageRef,
		tweenName: elementData.modifier?.["animate:"],
		navigation: url || toPage || backlink ? handleNavigation : undefined,
	});

	useLayoutEffect(() => {
		if (imageRef.current) {
			setImageWidth(imageRef.current.width);
		}
		allLoaded;
	}, [allLoaded]);

	return (
		<ThemeProvider theme={filter.image}>
			<StyledFigure
				$baseFontSize={baseFontSize}
				$baseFontSizeRaw={pageSize.baseFontSizeRaw}
				$elementIndex={atomId}
				$filterColors={filterColors}
				$fontMultiplier={fontMultiplier}
				$imageWidth={figureRef.current?.width}
				$isInFlexBox={isInFlexBox}
				$isInLandscape={pageSize.landscape}
				$modifier={elementData.modifier}
				$onePercentHeight={pageSize.onePercentHeight}
				$onePercentWidth={pageSize.onePercentWidth}
				$pageSize={pageSize}
				$isInPortrait={!pageSize.landscape}
				$tapped={tapped}
				aria-hidden={!elementData.tapped}
				className={`${strings.css.pageElement} animate-entry ${strings.css.dynamicResize} image`}
				data-atom-id={atomId}
				data-testid={`${type}${atomId}`}
				id={imageId}
				key={atomId}
				ref={figureRef}
			>
				<Image
					atomId={atomId}
					pageIndex={pageIndex}
					type={type}
					ref={imageRef}
					imageWidth={imageWidth}
					altText={elementData.altText}
				/>
				{caption && (
					<ThemeProvider theme={filter.caption}>
						<StyledCaption
							$absolutePosition={false}
							$baseFontSize={baseFontSize}
							$baseFontSizeRaw={pageSize.baseFontSizeRaw}
							$elementIndex={atomId}
							$filterColors={filterColors}
							$fontMultiplier={fontMultiplier}
							$isInFlexBox={isInFlexBox}
							$onePercentHeight={pageSize.onePercentHeight}
							$onePercentWidth={pageSize.onePercentWidth}
							$pageSize={pageSize}
							$isInPortrait={!pageSize.landscape}
							$tapped={tapped}
							$width={imageWidth}
							$parentFontSizeMultiplier={filter.box.fontSize}
						>
							<div>{caption}</div>
						</StyledCaption>
					</ThemeProvider>
				)}
			</StyledFigure>
		</ThemeProvider>
	);
};

export default memo(Figure);
